import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";

interface Course {
  id: string;
  name: string;
  price: number; // Price in cents from Stripe
  priceId: string;
  isAvailable: boolean;
}

interface CourseBundleSelectorProps {
  courses: Course[];
  onBundleSelect: (selectedCourses: Course[]) => void;
  formatPrice: (amountInCents: number) => string;
}

const CourseBundleSelector = ({ 
  courses, 
  onBundleSelect, 
  formatPrice 
}: CourseBundleSelectorProps) => {
  const [selectedCourses, setSelectedCourses] = useState<Course[]>([]);
  const bundleDiscount = 0.20; // 20% off
  const maxSelections = 2;

  // Check if all courses are available
  const allCoursesAvailable = courses.every(c => c.isAvailable);

  // Update selected courses when courses prop changes (e.g., billing period toggle)
  useEffect(() => {
    if (selectedCourses.length > 0) {
      // Update prices and priceIds for already-selected courses when courses prop changes
      const updatedSelectedCourses = selectedCourses.map(selectedCourse => {
        const matchingCourse = courses.find(course => course.id === selectedCourse.id);
        if (matchingCourse) {
          return {
            ...selectedCourse,
            price: matchingCourse.price,
            priceId: matchingCourse.priceId,
            isAvailable: matchingCourse.isAvailable,
          };
        }
        return selectedCourse;
      });
      
      // Only update if prices actually changed
      const pricesChanged = updatedSelectedCourses.some((updated, index) => 
        updated.price !== selectedCourses[index]?.price || 
        updated.priceId !== selectedCourses[index]?.priceId
      );
      
      if (pricesChanged) {
        setSelectedCourses(updatedSelectedCourses);
        onBundleSelect(updatedSelectedCourses);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses]); // Re-run when courses prop changes (selectedCourses is intentionally excluded to prevent loops)

  const toggleCourse = (course: Course) => {
    if (!course.isAvailable) return;
    
    setSelectedCourses(prev => {
      const isSelected = prev.some(c => c.id === course.id);
      
      if (isSelected) {
        // Deselect
        const newSelection = prev.filter(c => c.id !== course.id);
        onBundleSelect(newSelection);
        return newSelection;
      } else {
        // Select (but max 2)
        if (prev.length >= maxSelections) {
          return prev; // Already at max
        }
        const newSelection = [...prev, course];
        onBundleSelect(newSelection);
        return newSelection;
      }
    });
  };

  const totalPrice = selectedCourses.reduce((sum, course) => sum + course.price, 0);
  const discountAmount = Math.round(totalPrice * bundleDiscount);
  const bundlePrice = totalPrice - discountAmount;

  // If no courses are available, show unavailable message
  if (!allCoursesAvailable) {
    return (
      <div className="text-center py-8">
        <p 
          style={{
            color: "rgba(255, 255, 255, 0.6)",
            fontFamily: "'Grift', sans-serif",
          }}
        >
          Bundle currently unavailable. Please try again later.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Course Selection Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {courses.map((course) => {
          const isSelected = selectedCourses.some(c => c.id === course.id);
          const isDisabled = (selectedCourses.length >= maxSelections && !isSelected) || !course.isAvailable;

          return (
            <motion.button
              key={course.id}
              onClick={() => !isDisabled && toggleCourse(course)}
              disabled={isDisabled}
              className={`relative p-4 rounded-xl border-2 transition-all duration-300 ${
                isSelected
                  ? 'border-orange-500 bg-orange-500/10'
                  : isDisabled
                  ? 'border-gray-700 bg-gray-800/30 opacity-50 cursor-not-allowed'
                  : 'border-gray-700 bg-gray-800/30 hover:border-orange-500/50 cursor-pointer'
              }`}
              whileHover={!isDisabled && !isSelected ? { scale: 1.02 } : {}}
              whileTap={!isDisabled ? { scale: 0.98 } : {}}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 text-left">
                  <h4 
                    className="font-semibold mb-1"
                    style={{
                      fontFamily: "'Grift', sans-serif",
                      color: isSelected ? "#E67E22" : "#FFFFFF",
                    }}
                  >
                    {course.name}
                  </h4>
                  <p 
                    className="text-sm"
                    style={{
                      color: "rgba(255, 255, 255, 0.6)",
                      fontFamily: "'Grift', sans-serif",
                    }}
                  >
                    {course.isAvailable ? formatPrice(course.price) : "Unavailable"}
                  </p>
                </div>
                <div className="ml-4">
                  {isSelected ? (
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ background: "#22C55E" }}
                    >
                      <Check size={18} color="white" />
                    </div>
                  ) : (
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center border-2"
                      style={{ 
                        borderColor: "rgba(255, 255, 255, 0.3)",
                        background: "transparent"
                      }}
                    />
                  )}
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Bundle Pricing Display */}
      {selectedCourses.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 rounded-xl"
          style={{
            background: "linear-gradient(135deg, rgba(230, 126, 34, 0.15) 0%, rgba(255, 158, 79, 0.08) 100%)",
            border: "1px solid rgba(230, 126, 34, 0.3)",
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <p 
                className="text-sm mb-1"
                style={{
                  color: "rgba(255, 255, 255, 0.8)",
                  fontFamily: "'Grift', sans-serif",
                }}
              >
                Selected Courses ({selectedCourses.length}/2)
              </p>
              <p 
                className="text-xs"
                style={{
                  color: "rgba(255, 255, 255, 0.6)",
                  fontFamily: "'Grift', sans-serif",
                }}
              >
                {selectedCourses.length === 2 ? "20% bundle discount applied!" : "Select one more course for 20% off"}
              </p>
            </div>
            {selectedCourses.length === 2 && (
              <div 
                className="px-3 py-1 rounded-full text-sm font-semibold"
                style={{
                  background: "#22C55E",
                  color: "#000000",
                  fontFamily: "'Grift', sans-serif",
                }}
              >
                -20%
              </div>
            )}
          </div>

          <div className="space-y-2">
            {selectedCourses.map(course => (
              <div key={course.id} className="flex justify-between items-center">
                <span 
                  style={{
                    color: "rgba(255, 255, 255, 0.9)",
                    fontFamily: "'Grift', sans-serif",
                  }}
                >
                  {course.name}
                </span>
                <span 
                  style={{
                    color: "rgba(255, 255, 255, 0.7)",
                    fontFamily: "'Grift', sans-serif",
                  }}
                >
                  {formatPrice(course.price)}
                </span>
              </div>
            ))}
            
            {selectedCourses.length === 2 && (
              <>
                <div className="flex justify-between items-center pt-2 border-t border-orange-500/30">
                  <span 
                    style={{
                      color: "rgba(255, 255, 255, 0.8)",
                      fontFamily: "'Grift', sans-serif",
                    }}
                  >
                    Subtotal
                  </span>
                  <span 
                    style={{
                      color: "rgba(255, 255, 255, 0.8)",
                      fontFamily: "'Grift', sans-serif",
                    }}
                  >
                    {formatPrice(totalPrice)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span 
                    style={{
                      color: "#22C55E",
                      fontFamily: "'Grift', sans-serif",
                    }}
                  >
                    Bundle Discount (20%)
                  </span>
                  <span 
                    style={{
                      color: "#22C55E",
                      fontFamily: "'Grift', sans-serif",
                    }}
                  >
                    -{formatPrice(discountAmount)}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-orange-500/50">
                  <span 
                    className="text-xl font-bold"
                    style={{
                      color: "#E67E22",
                      fontFamily: "'Grift', sans-serif",
                    }}
                  >
                    Total
                  </span>
                  <span 
                    className="text-xl font-bold"
                    style={{
                      color: "#E67E22",
                      fontFamily: "'Grift', sans-serif",
                    }}
                  >
                    {formatPrice(bundlePrice)}
                  </span>
                </div>
              </>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default CourseBundleSelector;
